/**
 * contractFields — 공사계약서 공용 모델·포맷터 (순수 함수만 — vitest 대상).
 *
 * 구 ig-contract 단독앱 로직 승계. 표기 규칙(금액 콤마, 날짜 "____년 __월 __일"
 * 빈칸, 마지막 회차 반올림 오차 흡수, 02 지역번호 하이픈)은 원본 PDF 동일 재현이
 * 목적이므로 임의로 바꾸지 말 것.
 */

/** 금액 콤마 표기 */
/** 지면 금액 표기 — BE `_won→_amt_raw` 와 **같은 정규화**(전수검증 V3 ★).
 *  예전엔 원문 그대로 찍어 "11000000"(콤마 없음)·날짜 문자열("2026-07-08 원")이
 *  표지에 그대로 나갔다 — BE PDF 와 지면이 갈리는 미러 결함. */
export function won(n) {
  const s = String(n ?? "").replace(/[,\s₩￦원]/g, "");
  const v = /^-?\d+(\.\d+)?$/.test(s) ? Math.trunc(Number(s)) : 0;
  return v.toLocaleString("ko-KR");
}

/** 문자열에서 숫자만 → 정수 */
export function numOnly(s) {
  return parseInt(String(s).replace(/[^\d]/g, ""), 10) || 0;
}

/** 금액 정규화 — 콤마·'원' 섞인 문자열도 수용.
 *
 * ⚠`Number("11,000,000")` 은 NaN 이라 `|| 0` 으로 **0원**이 됐다. 백엔드는
 *   `_amt_raw` 로 콤마를 수용하므로 같은 계약에서 FE 총액만 0 이 되어 갈렸다
 *   (PDF 실렌더 관측 2026-07-30: 표지 "일금 ￦ 0 원"). 금액 파생은 전부 이걸 거친다.
 */
export function amtNum(v) {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  // ⚠숫자 아닌 문자를 '걷어내는' 방식은 위험하다 — "2026-07-08"(날짜 오입력)이
  //   2026 이 된다. 백엔드 `_amt_raw` 는 같은 입력에서 float() 가 터져 0 을 내므로
  //   동형화가 깨진다. 콤마·공백·원/₩ 만 제거한 뒤 **전체가 숫자일 때만** 인정한다.
  const s = String(v ?? "").replace(/[,\s₩￦]/g, "").replace(/원$/, "");
  return /^-?\d+(\.\d+)?$/.test(s) ? parseFloat(s) : 0;
}

/** 부가세 포함 금액 = 공급가 × 1.1 반올림 */
export function inclAmount(amtSupply) {
  return Math.round(amtNum(amtSupply) * 1.1);
}

/** 계약 총액(부가세 포함) — vatIncluded 계약(부가세 포함으로 협의, 2026-07-21)은
 * amtSupply 가 이미 포함가라 그대로. 총액 파생 계산은 전부 이걸 거친다(BE _contract_incl 미러). */
export function contractIncl(d) {
  const o = d || {};
  return o.vatIncluded ? Math.round(amtNum(o.amtSupply)) : inclAmount(o.amtSupply);
}

/** 전자계약 목록 행(snake_case: amt_supply/vat_included)용 계약 총액 */
export function rowIncl(r) {
  const o = r || {};
  return o.vat_included ? Math.round(amtNum(o.amt_supply)) : inclAmount(o.amt_supply);
}

/** 연락처 하이픈 — 02 지역번호(서울) 예외 포함 (구 앱 fmtTel 동일) */
export function fmtContractTel(raw) {
  let n = String(raw).replace(/[^\d]/g, "");
  if (n.length > 11) n = n.slice(0, 11);
  if (n.startsWith("02")) {
    if (n.length <= 2) return n;
    if (n.length <= 5) return n.replace(/(\d{2})(\d+)/, "$1-$2");
    if (n.length <= 9) return n.replace(/(\d{2})(\d{3})(\d+)/, "$1-$2-$3");
    return n.replace(/(\d{2})(\d{4})(\d{4})/, "$1-$2-$3");
  }
  if (n.length <= 3) return n;
  if (n.length <= 7) return n.replace(/(\d{3})(\d+)/, "$1-$2");
  // 10자리 유선(031-123-4567)은 3-3-4 — 3-4-3 으로 끊으면 '031-1234-567' 이 된다
  if (n.length === 10) return n.replace(/(\d{3})(\d{3})(\d{4})/, "$1-$2-$3");
  return n.replace(/(\d{3})(\d{4})(\d+)/, "$1-$2-$3");
}

/** '2026-07-08' → {y,m,d} (빈값은 수기 빈칸용 언더스코어) */
export function fmtDateK(iso) {
  if (!iso) return { y: "____", m: "__", d: "__" };
  const [y, m, d] = String(iso).split("-");
  return { y, m: String(+m).padStart(2, "0"), d: String(+d).padStart(2, "0") };
}

/** '2026-07-08' → '2026년 07월 08일' (빈값='____년 __월 __일') */
export function dateBlank(iso) {
  const k = fmtDateK(iso);
  return iso ? `${k.y}년 ${k.m}월 ${k.d}일` : "____년 __월 __일";
}

/** 공사기간(일) — 착공일·준공일 양끝 포함("A 부터 B 까지"), 공정표(ig-ism)와 같은 셈.
 *  예: 10-14 ~ 10-25 = 12일 (예전엔 차이만 세어 11일 — 사장 2026-10-09). 빈값이면 0 */
export function periodDays(a, b) {
  if (!a || !b) return 0;
  return Math.round((new Date(b) - new Date(a)) / 86400000) + 1;
}

/** a~b 를 비율 t(0~1)로 보간한 ISO 날짜 */
export function dateLerp(a, b, t) {
  if (!a || !b) return "";
  const ta = new Date(a).getTime();
  const tb = new Date(b).getTime();
  return new Date(ta + (tb - ta) * t).toISOString().slice(0, 10);
}

/**
 * 회차 금액 배열.
 * - percent(레거시 기본): 부가세 포함액 기준, 반올림 오차는 마지막 회차에 흡수
 * - amount: 각 행의 원 단위 amount 가 정본이며 마지막 행도 임의 보정하지 않음
 */
export function payAmounts(payRows, incl, payMode = "percent") {
  const rows = payRows || [];
  if (payMode === "amount") {
    return rows.map((r) => Math.round(amtNum(r.amount)));
  }
  let acc = 0;
  return rows.map((r, i) => {
    if (i === rows.length - 1) return incl - acc;
    const a = Math.round(incl * (Number(r.pct) || 0) / 100);
    acc += a;
    return a;
  });
}

/** 지면에 찍을 회차 % — 마지막 회차는 실효값 (BE _pay_pcts 미러, 백로그 #41).
 *
 * 금액은 마지막 회차가 잔여 전액을 흡수하는데 % 는 입력값 그대로 찍혀,
 * 비율 합이 100 이 아니면 "9,900,000 (10%)" 처럼 금액·% 모순인 채 서명됐다.
 * 합이 100(±0.01)이면 입력 그대로(기존 지면 불변).
 */
export function payPcts(payRows, incl = 0, payMode = "percent") {
  const rows = payRows || [];
  if (payMode === "amount") {
    const total = Math.round(amtNum(incl));
    return rows.map((r) => {
      if (!total) return "0";
      const pct = Math.round((Math.round(amtNum(r.amount)) / total) * 10_000) / 100;
      return String(pct);
    });
  }
  const sum = rows.reduce((a, r) => a + (Number(r.pct) || 0), 0);
  return rows.map((r, i) => {
    if (i === rows.length - 1 && Math.abs(sum - 100) > 0.01) {
      const eff = 100 - (sum - (Number(r.pct) || 0));
      return String(Number.isInteger(eff) ? eff : Math.round(eff * 100) / 100);
    }
    return String(r.pct ?? "");
  });
}

/** 회차 비율 합 */
export function paySum(payRows) {
  return (payRows || []).reduce((a, r) => a + (Number(r.pct) || 0), 0);
}

/** 고정금액 회차 합계(원 단위). */
export function payAmountSum(payRows) {
  return (payRows || []).reduce((sum, r) => sum + Math.round(amtNum(r.amount)), 0);
}

/**
 * 결제 회차 합계 검증. amount 모드는 계약 총액과 원 단위로 정확히 같아야 한다.
 * payMode 가 없거나 알 수 없는 레거시 문서는 percent 로 해석한다.
 */
export function payScheduleCheck(data) {
  const d = data || {};
  const mode = d.payMode === "amount" ? "amount" : "percent";
  const target = mode === "amount" ? contractIncl(d) : 100;
  const total = mode === "amount" ? payAmountSum(d.payRows) : paySum(d.payRows);
  const difference = target - total;
  const populated = mode !== "amount" || (target > 0 && (d.payRows || []).length > 0);
  return { mode, target, total, difference, valid: populated && Math.abs(difference) < 0.000001 };
}

const roundPct = (value) => Math.round(value * 100) / 100;

/** 결제 입력 방식 전환. 변환 대상 필드만 채우고 원본 행은 변경하지 않는다. */
export function convertPayRows(payRows, incl, fromMode = "percent", toMode = "percent") {
  const rows = payRows || [];
  const from = fromMode === "amount" ? "amount" : "percent";
  const to = toMode === "amount" ? "amount" : "percent";
  if (from === to) return rows.map((r) => ({ ...r }));
  if (to === "amount") {
    const amounts = payAmounts(rows, incl, "percent");
    return rows.map((r, i) => ({ ...r, amount: amounts[i] }));
  }

  const total = Math.round(amtNum(incl));
  if (!total) return rows.map((r) => ({ ...r, pct: 0 }));
  const amounts = payAmounts(rows, total, "amount");
  const exactTotal = amounts.reduce((sum, value) => sum + value, 0) === total;
  let pctAcc = 0;
  return rows.map((r, i) => {
    const pct = exactTotal && i === rows.length - 1
      ? roundPct(100 - pctAcc)
      : roundPct((amounts[i] / total) * 100);
    pctAcc += pct;
    return { ...r, pct };
  });
}

/** 회차 행 전체를 불변 재정렬. 범위를 벗어나면 동일 순서의 새 배열을 반환한다. */
export function movePayRow(payRows, fromIndex, toIndex) {
  const rows = Array.isArray(payRows) ? payRows : [];
  const next = rows.slice();
  if (!Number.isInteger(fromIndex) || !Number.isInteger(toIndex)
      || fromIndex < 0 || fromIndex >= next.length || toIndex < 0 || toIndex >= next.length
      || fromIndex === toIndex) return next;
  const [row] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, row);
  return next;
}

/**
 * 결제일 자동반영 — 새 rows 반환(불변).
 * 계약금=계약일, 착수금=착공, 중도금=착공~준공 균등분할(1/(N+1)…), 잔금=준공.
 */
export function autofillPayDates(payRows, { signDate, startDate, endDate }) {
  const midTotal = (payRows || []).filter((r) => r.name.includes("중도금")).length;
  let midIdx = 0;
  return (payRows || []).map((r) => {
    const nm = r.name;
    if (nm.includes("계약금")) return { ...r, date: signDate || "" };
    if (nm.includes("착수금")) return { ...r, date: startDate || "" };
    if (nm.includes("중도금")) {
      midIdx++;
      return { ...r, date: dateLerp(startDate, endDate, midIdx / (midTotal + 1)) };
    }
    if (nm.includes("잔금")) return { ...r, date: endDate || "" };
    return r;
  });
}

/** ts → 'YYMMDD' */
export function yymmdd(ts) {
  const d = new Date(ts);
  return String(d.getFullYear()).slice(2)
    + String(d.getMonth() + 1).padStart(2, "0")
    + String(d.getDate()).padStart(2, "0");
}

/** 공정표(iggg-schedule)와 동일한 문서 키 정규화 규칙 — 바꾸면 기존 문서와 어긋남 */
export function sanitizeKey(s) {
  return String(s || "").trim().replace(/[^a-zA-Z0-9가-힣ㄱ-ㅎㅏ-ㅣ\-_]/g, "_").substring(0, 100);
}

/** 저장 키: 기존 동일 현장명 키 재사용, 없으면 'YYMMDD 현장명' (구 앱 규칙) */
export function contractKey(d, existingStore) {
  const store = existingStore || {};
  const existKey = Object.keys(store).find((k) => store[k].siteName === d.siteName);
  return existKey || `${yymmdd(d.savedAt || Date.now())} ${d.siteName}`;
}

/** PDF 파일명: 'YYMMDD 현장명 계약서(진현|이견)' — 날짜는 계약일 우선 */
export function contractFileBase(d, now = new Date()) {
  const base = d.signDate ? d.signDate.slice(2).replace(/-/g, "") : yymmdd(now.getTime());
  const vTag = d.vendor === "igyeon" ? "(이견)" : "(진현)";
  return `${base} ${d.siteName || "계약서"} 계약서${vTag}`;
}

/**
 * 생성예정 제외 판정 — 일정표 현장 name 이 계약서 저장소에 "작성됨"으로 덮이는가.
 * 현장명은 자유 편집 필드라(발주자 상호로 바꾸는 실사용) siteName 완전일치만으로는
 * 링크가 끊긴다(260613_논현동_조광 실결함). 셋 중 하나면 작성됨:
 *   ① siteName(트림) 일치  ② sourceSite(작성 진입 시 저장한 원천 링크) 일치
 *   ③ 문서 키 폴백 — 키의 YYMMDD_ 프리픽스 제거 == sanitizeKey(현장명)
 *      (키는 최초 저장 시점 현장명으로 고정되므로 이후 개명해도 남는 흔적)
 */
export function siteCovered(contracts, name) {
  const target = String(name || "").trim();
  if (!target) return false;
  const targetKey = sanitizeKey(target);
  return Object.entries(contracts || {}).some(([key, d]) =>
    String((d && d.siteName) || "").trim() === target
    || String((d && d.sourceSite) || "").trim() === target
    || key.replace(/^\d{6}_/, "") === targetKey);
}

/** 새 계약서 기본값 */
export function emptyContractData() {
  return {
    vendor: "jinhyun",
    siteName: "", siteAddr: "", siteTel: "",
    sourceSite: "",       // 생성예정→작성 진입 시 일정표 현장명(개명해도 유지되는 링크)
    amtSupply: 0,
    vatIncluded: false,   // true = 설계 금액이 부가세 포함가(지면 "(부가세포함)")
    signDate: "", days: "", startDate: "", endDate: "",
    payMode: "percent",  // percent(레거시 기본) | amount(원 단위 고정금액)
    payRows: [],          // {name, pct, amount, date}; payMode 쪽 필드가 정본
    clauseEdits: {},      // {clauseKey: 수정본문} — 고정 조항 수동 수정본
    special: "",
    savedAt: 0,
  };
}
